/**
 * useFileStream — file streaming state and refs
 * Phase 4 (PRI-291): extracted from useStreamSession
 *
 * Owns: isFileStream, fileDuration, fileCurrentTime (state)
 *       fileVideoRef, fileObjectUrlRef, fileBlobRef, fileTimeUpdateIntervalRef, isFileStreamRef (refs)
 *
 * The startFileStream() function stays in the orchestrator because it calls startStream()
 * and cancelPreConnect(), which are orchestrator-level concerns.
 */

import { useState, useRef } from 'react'

export interface UseFileStreamReturn {
  /** Whether a video file is currently being streamed (not a webcam) */
  isFileStream: boolean
  setIsFileStream: React.Dispatch<React.SetStateAction<boolean>>
  /** Duration of the loaded video file in seconds */
  fileDuration: number
  setFileDuration: React.Dispatch<React.SetStateAction<number>>
  /** Current playback position of the video file in seconds */
  fileCurrentTime: number
  setFileCurrentTime: React.Dispatch<React.SetStateAction<number>>
  /** Hidden <video> element that plays the file — owned here, removed on cleanup */
  fileVideoRef: React.MutableRefObject<HTMLVideoElement | null>
  /** Object URL created for the file blob — revoked on cleanup */
  fileObjectUrlRef: React.MutableRefObject<string | null>
  /** The original file/blob — kept for the non-SAM optimization (reuse original as recording) */
  fileBlobRef: React.MutableRefObject<File | Blob | null>
  /** setInterval handle for updating fileCurrentTime every 250 ms */
  fileTimeUpdateIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  /** Ref mirror of isFileStream state — safe to read inside stale closures */
  isFileStreamRef: React.MutableRefObject<boolean>
}

export function useFileStream(): UseFileStreamReturn {
  const [isFileStream, setIsFileStreamState] = useState<boolean>(false)
  const [fileDuration, setFileDuration] = useState<number>(0)
  const [fileCurrentTime, setFileCurrentTime] = useState<number>(0)

  const fileVideoRef = useRef<HTMLVideoElement | null>(null)
  const fileObjectUrlRef = useRef<string | null>(null)
  const fileBlobRef = useRef<File | Blob | null>(null)
  const fileTimeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isFileStreamRef = useRef<boolean>(false)

  // Keep ref in sync with state so stale closures always read the latest value.
  const setIsFileStream: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const next = typeof value === 'function' ? value(isFileStreamRef.current) : value
    isFileStreamRef.current = next
    setIsFileStreamState(next)
  }

  return {
    isFileStream,
    setIsFileStream,
    fileDuration,
    setFileDuration,
    fileCurrentTime,
    setFileCurrentTime,
    fileVideoRef,
    fileObjectUrlRef,
    fileBlobRef,
    fileTimeUpdateIntervalRef,
    isFileStreamRef,
  }
}
